import {
  GameVariant,
  Move,
  Piece,
  PieceType,
  VARIANT_CONFIGS,
  VARIANT_SPECS,
} from '@draughts/shared';
import { squareToPosition } from '../board/square-utils.js';
import { willPromote } from './simple-moves.js';

/**
 * LA PROMOZIONE AL VOLO: in quale salto compare la DAMA, durante la sequenza di presa.
 *
 * Serve a chi DISEGNA. Nella Russa una pedina che tocca l'ultima traversa a meta' presa
 * e' dama DA QUEL SALTO — `capture-moves.ts` la manda in `findKingCaptures(...,
 * promoted = true)` e la sequenza prosegue da dama volante — e il fantasma che percorre
 * la presa deve portare la corona da li', non all'arrivo. Il generatore quel salto lo
 * conosce e lo butta: la `Move` porta solo `isPromotion` e `path`. Qui lo si ritrova
 * dalla geometria.
 *
 * `null` SU TUTTE LE ALTRE VARIANTI, ed e' la meta' che conta. Dove la promozione non e'
 * al volo la dama non compare MAI durante la sequenza: la damiera viva la mostra
 * all'arrivo, e un fantasma incoronato a meta' strada racconterebbe una regola che non
 * esiste. Nell'Internazionale (e nella Turca, e nella Pool) la pedina ATTRAVERSA
 * l'ultima traversa e resta pedina; nell'Italiana la presa si FERMA la', e l'ultimo
 * salto e' gia' la damiera viva. In nessuno dei due casi il fantasma disegna quel salto.
 *
 * Tre scelte che sembrano dimenticanze e non lo sono:
 *
 * 1. La risposta dipende solo dal PREFISSO dei salti, mai dalla coda. Chi chiama ha in
 *    mano un elenco di CANDIDATE che concordano sui salti gia' percorsi ma non su come
 *    continuano (`captureInProgress.possibleMoves`): una risposta che guardasse la coda
 *    cambierebbe col candidato che capita primo.
 * 2. `isPromotion` non entra nemmeno fra gli argomenti. Con `promoteInPassing` la regola
 *    e' incondizionata — atterri sull'ultima traversa, sei dama — quindi la geometria
 *    basta, e una `Move` arrivata dal server viene disegnata secondo la REGOLA invece che
 *    secondo la bandierina. (Nella Russa `isPromotion: true` con un `to` che NON e'
 *    sull'ultima traversa e' il caso NORMALE, non un errore.)
 * 3. L'origine non si guarda, come non la guarda il generatore, che prova `willPromote`
 *    solo sulle caselle di ATTERRAGGIO: una pedina messa a mano sull'ultima traversa
 *    (editor, FEN) resta pedina finche' non ci atterra.
 *
 * @returns l'indice nei salti (`move.path`, e per una presa di un salto solo `[move.to]`)
 *          in cui la pedina diventa dama, oppure `null`.
 */
export function promotionHopInPassing(
  move: Pick<Move, 'path' | 'to'>,
  piece: Pick<Piece, 'color' | 'type'>,
  variant: GameVariant,
): number | null {
  if (piece.type !== PieceType.MAN) return null;

  const rules = VARIANT_SPECS[variant].rules;
  /*
   * L'ordine e' quello del generatore (`capture-moves.ts`: un `if / else if`):
   * `promotionStopsCapture` ha la precedenza, e la spec dice che `promoteInPassing` ha
   * senso solo quando la prima e' falsa. Oggi nessuna variante le ha entrambe.
   */
  if (rules.promotionStopsCapture || !rules.promoteInPassing) return null;

  const config = VARIANT_CONFIGS[variant];
  const allSquares = config.playableSquares === config.size * config.size;
  const hops = move.path ?? [move.to];

  for (let i = 0; i < hops.length; i++) {
    const { row } = squareToPosition(hops[i]!, config.size, config.darkSquareBottomRight, allSquares);
    if (willPromote(piece.color, row, config.size)) return i;
  }

  return null;
}
